import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LOGIN_SLIDES from './loginSlides';

const API_BASE = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001';

/* Cross-fading slideshow — the same construction / before-after / team photos as
   Facility Navigator. Fills its (relatively-positioned) parent, so it lives in the
   left panel of the split sign-in. Slides mount progressively so the full ~11 MB
   set isn't fetched up front. No-ops if the list is empty. */
function SlideshowBackground() {
  const slides = LOGIN_SLIDES;
  const [i, setI] = useState(0);
  const [mounted, setMounted] = useState(() => Math.min(2, slides.length));

  useEffect(() => {
    if (slides.length < 2) return undefined;
    const id = setInterval(() => setI((n) => (n + 1) % slides.length), 6500);
    return () => clearInterval(id);
  }, [slides.length]);

  useEffect(() => {
    setMounted((m) => Math.min(slides.length, Math.max(m, i + 2)));
  }, [i, slides.length]);

  if (slides.length === 0) return null;
  return (
    <div aria-hidden style={{ position: 'absolute', inset: 0, overflow: 'hidden', backgroundColor: '#20261a' }}>
      {slides.slice(0, mounted).map((src, idx) => (
        <img
          key={src}
          src={src}
          alt=""
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'cover',
            opacity: idx === i ? 1 : 0,
            transition: 'opacity 1.6s ease-in-out',
          }}
        />
      ))}
      {/* Subtle olive scrim, matching FN's tinted photo panel. */}
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(28,40,20,0.20)' }} />
    </div>
  );
}

const LoginPage = ({ onLogin }) => {
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Pick up Entra callback params (token + user) or an error from the URL.
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const userData = params.get('user');
    const urlError = params.get('error');

    if (token && userData) {
      localStorage.setItem('e3_auth_token', token);
      localStorage.setItem('e3_user', userData);
      const stashed = sessionStorage.getItem('login_return_to');
      sessionStorage.removeItem('login_return_to');
      const returnTo = stashed && stashed.startsWith('/') && !stashed.startsWith('//')
        ? stashed
        : '/';
      // Hard navigation rather than SPA navigate: auth is already in
      // localStorage, so a full reload at returnTo boots the app logged in.
      window.location.replace(returnTo);
      return;
    }

    if (urlError) {
      setError(decodeURIComponent(urlError));
      navigate('/', { replace: true });
    }
  }, [onLogin, navigate]);

  const handleEntraLogin = () => {
    // Remember where to land after the Entra round-trip wipes the SPA state.
    sessionStorage.setItem(
      'login_return_to',
      window.location.pathname + window.location.search + window.location.hash
    );
    window.location.href = `${API_BASE}/api/auth/entra/login`;
  };

  return (
    <div className="e3-login">
      {/* Layout + styling copied from E3's actual CIAM branding
          (app-rebuild/infra/branding/customerciam-signin.css + signin-fixture.html):
          photo pane flex:2, white pane flex:1, a 412px sign-in box centered + top. */}
      <style>{`
        .e3-login { min-height: 100vh; display: flex; background: #faf9f7; }
        .e3-login-photos { position: relative; flex: 2 1 0; overflow: hidden; background: #2b4a24; }
        .e3-login-right { flex: 0 0 472px; background: #ffffff; display: flex; align-items: flex-start; justify-content: center; padding: 40px 30px 24px; }
        .e3-login-box {
          width: 412px; max-width: 100%; box-sizing: border-box; background: #ffffff;
          border: 1px solid #ece7dd; border-radius: 16px; padding: 42px 40px 34px;
          box-shadow: 0 1px 3px rgba(31,29,26,.05), 0 18px 44px -18px rgba(30,52,24,.2);
        }
        .e3-signin { transition: background-color .13s ease, transform .05s ease; }
        .e3-signin:hover { background-color: #4d6a40 !important; border-color: #4d6a40 !important; }
        .e3-signin:active { background-color: #41593a !important; transform: translateY(1px); }
        @media (max-width: 820px) {
          .e3-login { flex-direction: column; }
          .e3-login-photos { flex: 0 0 26vh; }
          .e3-login-right { flex: 1 1 auto; align-items: stretch; padding: 22px 16px; }
        }
      `}</style>

      <div className="e3-login-photos">
        <SlideshowBackground />
      </div>

      <div className="e3-login-right">
        <div className="e3-login-box">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 26 }}>
            <img src="/logo.png" alt="" style={{ width: 28, height: 28, objectFit: 'contain', borderRadius: 6, display: 'block' }} />
            <span style={{ fontSize: 15, fontWeight: 700, color: '#1b1b1b', letterSpacing: '-.01em' }}>E3 Summit</span>
          </div>

          <h1 style={{ margin: 0, fontSize: 29, fontWeight: 700, color: '#1e3418', letterSpacing: '-.022em', lineHeight: 1.08 }}>
            Sign in
          </h1>
          <p style={{ margin: '8px 0 0', fontSize: 14.5, fontWeight: 450, lineHeight: 1.45, color: '#555048' }}>
            Sign in to access E3 Summit
          </p>

          {error && (
            <div style={{ marginTop: 22, fontSize: 13, color: '#a4462e' }}>
              {error}
            </div>
          )}

          <button onClick={handleEntraLogin} className="e3-signin" style={{
            marginTop: 26, width: '100%', height: 44, borderRadius: 10,
            border: '1px solid #5a7a4d', backgroundColor: '#5a7a4d', color: '#ffffff',
            fontSize: 15, fontWeight: 600, letterSpacing: '-.005em', cursor: 'pointer',
            boxShadow: '0 1px 2px rgba(30,52,24,.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            Sign in with Microsoft
          </button>

          <p style={{ margin: '40px 0 0', fontSize: 12.5, fontWeight: 450, lineHeight: 1.65, color: '#555048' }}>
            Sign in to E3 Summit to view your projects, billing, and job costs. Access is provided by E3 Entegral Solutions — if you can’t sign in, contact your administrator.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
